import { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import Eyebrow from './Eyebrow';
import { api } from '../api';

const ease = [0.22, 1, 0.36, 1];

const STEP_STYLES = [
  { gradient: 'linear-gradient(135deg, #1d4ed8 0%, #4f46e5 100%)', light: '#eff6ff' },
  { gradient: 'linear-gradient(135deg, #5E0ED7 0%, #7c3aed 100%)', light: '#f5f3ff' },
  { gradient: 'linear-gradient(135deg, #7c3aed 0%, #a21caf 100%)', light: '#fdf4ff' },
  { gradient: 'linear-gradient(135deg, #d97706 0%, #ea580c 100%)', light: '#fffbeb' },
  { gradient: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)', light: '#ecfdf5' },
  { gradient: 'linear-gradient(135deg, #0891b2 0%, #0284c7 100%)', light: '#f0f9ff' },
];

const DEFAULT_STEPS = [
  { num: '01', title: 'Discover & Define',      desc: 'Deep stakeholder interviews, competitive analysis, and technical audits to define the problem before proposing solutions.' },
  { num: '02', title: 'Strategy & Architecture', desc: 'Product roadmap, tech stack selection, system architecture, and success metrics aligned to your business outcomes.' },
  { num: '03', title: 'Design & Prototype',      desc: 'High-fidelity prototypes and design systems. Every interaction validated with real users before a line of code is written.' },
  { num: '04', title: 'Build & Iterate',         desc: 'Agile sprints. Working software every 2 weeks — gather feedback, iterate fast, minimize risk, maximize value.' },
  { num: '05', title: 'Launch & Optimize',       desc: 'Zero-downtime deployments, monitoring setup, and post-launch optimization to ensure performance and adoption.' },
  { num: '06', title: 'Scale & Evolve',          desc: 'Ongoing engineering support, feature development, and technology evolution to keep your product ahead of the curve.' },
];

function enrichStep(s, i) {
  return { ...STEP_STYLES[i % STEP_STYLES.length], ...s };
}

function MobileStep({ step, index, isLast }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -20 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay: index * 0.08, duration: 0.5, ease }}
      className="flex gap-4"
    >
      <div className="flex flex-col items-center">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-[13px] font-bold shrink-0 z-10" style={{ background: step.light, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          <span style={{ background: step.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{step.num}</span>
        </div>
        {!isLast && <div className="w-px flex-1 my-2" style={{ background: 'rgba(24,24,27,0.08)', minHeight: 24 }} />}
      </div>
      <div className="flex-1 pb-6">
        <div className="rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 12px -4px rgba(0,0,0,0.08)' }}>
          <div className="h-1 w-full" style={{ background: step.gradient }} />
          <div className="bg-white p-5">
            <h3 className="text-[14px] font-bold mb-2 tracking-tight" style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{step.title}</h3>
            <p className="text-xs leading-relaxed" style={{ color: '#6B6B72' }}>{step.desc}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function StepCard({ step, index, total }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const isLast = index === total - 1;

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      {/* Connector line (not on last) */}
      {!isLast && (
        <div className="hidden lg:block absolute top-10 left-full z-10 w-full" style={{ width: 'calc(100% - 80%)' }}>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ delay: index * 0.12 + 0.4, duration: 0.5, ease }}
            className="h-px"
            style={{ background: 'linear-gradient(to right, rgba(94,14,215,0.5), rgba(94,14,215,0.1))', transformOrigin: 'left center', width: '100%' }}
          />
          {/* Arrow head */}
          <motion.div
            initial={{ opacity: 0, x: -4 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: index * 0.12 + 0.7, duration: 0.3 }}
            className="absolute right-0 top-1/2 -translate-y-1/2"
            style={{ width: 0, height: 0, borderTop: '4px solid transparent', borderBottom: '4px solid transparent', borderLeft: '6px solid rgba(94,14,215,0.4)' }}
          />
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: index * 0.1, duration: 0.55, ease }}
        whileHover={{ y: -4 }}
        className="h-full"
      >
        <div className="rounded-2xl overflow-hidden h-full" style={{ boxShadow: '0 4px 20px -6px rgba(0,0,0,0.10)' }}>
          {/* Colored top strip */}
          <div className="h-1.5 w-full" style={{ background: step.gradient }} />

          {/* Card body */}
          <div className="bg-white p-5 flex flex-col gap-3 h-full" style={{ minHeight: 200 }}>
            {/* Step number badge */}
            <div className="flex items-center gap-3">
              <span
                className="w-10 h-10 rounded-xl flex items-center justify-center text-[13px] font-bold shrink-0"
                style={{ background: step.light, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                <span style={{ background: step.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {step.num}
                </span>
              </span>
              {/* Dot connector on mobile (vertical) */}
              <div className="lg:hidden w-px flex-1 bg-gray-100" />
            </div>

            <h3 className="text-[14px] font-bold tracking-tight leading-snug" style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {step.title}
            </h3>
            <p className="text-xs leading-relaxed" style={{ color: '#6B6B72' }}>{step.desc}</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function Methodology() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const [rawSteps, setRawSteps] = useState(DEFAULT_STEPS);
  const steps = rawSteps.map(enrichStep);

  useEffect(() => {
    api.getMethodology().then(d => { if (Array.isArray(d) && d.length) setRawSteps(d); }).catch(() => {});
  }, []);

  return (
    <section id="process" className="px-5 sm:px-8 md:px-12 lg:px-20 flex flex-col justify-center py-24 section-gray" style={{ minHeight: '100dvh' }}>
      <div className="max-w-7xl mx-auto w-full">
        <div ref={ref} className="mb-14">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, ease }}>
            <Eyebrow>Our Process</Eyebrow>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.08, ease }}
            className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight max-w-2xl leading-[1.08]"
            style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.025em' }}
          >
            How We Deliver
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.16, ease }}
            className="text-base mt-3 max-w-xl leading-relaxed"
            style={{ color: '#6B6B72' }}
          >
            A proven 6-step process that transforms your idea into a shipped, scalable product.
          </motion.p>
        </div>

        {/* ── Desktop: horizontal workflow ── */}
        <div className="hidden lg:flex items-stretch gap-4 relative">
          {/* Background track line */}
          <div className="absolute top-10 left-10 right-10 h-px" style={{ background: 'rgba(24,24,27,0.06)' }} />
          {steps.map((s, i) => (
            <StepCard key={s.num} step={s} index={i} total={steps.length} />
          ))}
        </div>

        {/* ── Mobile: vertical timeline ── */}
        <div className="lg:hidden flex flex-col gap-0">
          {steps.map((step, i) => (
            <MobileStep key={step.num} step={step} index={i} isLast={i === steps.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
